import { Routes, Route } from "react-router-dom";
import { Navigate, useLocation } from "react-router-dom";
import { Container } from "react-bootstrap";
import { Store } from "./layout/Store";
import { Orders } from "./layout/Orders";
import { Navbar } from "./components/Navbar";
import { ShoppingCartProvider } from "./context/ShoppingCartContext";
import { ServiceAvailabilityProvider } from "./context/ServiceAvailabilityContext";
import { UserContextProvider, useUserContext } from "./context/UserContext";
import { saveReturnPath } from "./utilities/authRedirect";
import { TabContextProvider } from "./context/TabContext";
import { ThemeProvider } from "./context/ThemeContext";
import { QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense, useEffect } from "react";
import { queryClient } from "./lib/query/queryClient";
import MobileWrapper from "./components/MobileWrapper";
import "./styles/theme.css";
import "./globals.css";
import "./styles/mobile.css";

import Footer from "./components/Footer";
import Checkout from "./layout/Checkout";
import Login from "./layout/auth/login/Login";
import Register from "./layout/auth/signup/Register";
import SupLogin from "./layout/elite/sup/auth/login/Login";
// import SupRegister from "./layout/elite/sup/auth/signup/Register";
import OperationsRegister from "./layout/elite/ops/auth/signup/Register";
import OperationsLogin from "./layout/elite/ops/auth/login/Login";
import SupMain from "./layout/elite/sup/SupMain";
import OpsHome from "./layout/elite/ops/main";
import StoreInfo from "./layout/StoreInfo";
import VerificationPage from "./layout/auth/signup/VerifyEmail";
import ResetPassword from "./layout/auth/login/ResetPassword";
import Deliveries from "./layout/elite/ops/Deliveries";
import LiquorStoreInfo from "./layout/LiquorStoreInfo";
import LiquorStoreProducts from "./layout/LiquorStoreProducts";
import OrdersSummary from "./layout/elite/sup/OrdersSummary";
import StoresSummary from "./layout/elite/sup/StoresSummary";
import Product from "./layout/elite/store/Product";
import AddProductForm from "./layout/elite/store/AddProduct";
import AddOption from "./layout/elite/store/AddOption";
import EditProduct from "./layout/elite/store/EditProduct";
import EditOption from "./layout/elite/store/EditOption";
import TransactionInProgress from "./layout/TransactionInProgress";
import Referrals from "./layout/Referrals";
import SecuritySettings from "./layout/Security/Security";
import PreferencesSettings from "./layout/Preferences";
import { About } from "./layout/About";
import { Contact } from "./layout/Contact";
import { Privacy } from "./layout/Privacy";
import { Terms } from "./layout/Terms";
import TransactionProgress from "./layout/elite/ops/TransactionProgress";
import AddStoreForm from "./layout/elite/sup/AddStore";
import SafetyGuidelines from "./layout/SafetyGuidelines";
import StoreLogin from "./layout/elite/store/auth/login/login";
import StoreRegister from "./layout/elite/store/auth/signup/register";
import StoreDashboard from "./layout/elite/store/main";
import StoreOrders from "./layout/elite/store/Orders";
import CapacitorTest from "./layout/CapacitorTest";
import Catalog from "./layout/elite/store/Catalog";
import Account from "./layout/Account";
import Products from "./layout/elite/sup/Products";

// Dynamically imported so the devtools bundle (and the `import.meta.env.DEV`
// check gating it) is dropped entirely from the production build rather than
// merely hidden — Vite dead-code-eliminates the whole branch when DEV is
// statically false.
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import("@tanstack/react-query-devtools").then((m) => ({
        default: m.ReactQueryDevtools,
      }))
    )
  : null;

const App = () => {
  return (
    <UserContextProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <MobileWrapper>
            <div className="d-flex flex-column min-vh-100 bg-body capacitor-app">
              <ServiceAvailabilityProvider>
              <ShoppingCartProvider>
                <TabContextProvider>
                  <Navbar />
                  <Container className="mb-4 3 main-content">
                    <Routes>
                      <Route path="/" element={<Store />} />
                      <Route path="/stores" element={<Navigate to="/" replace />} />
                      <Route path="/about" element={<About />} />
                      <Route path="/contact" element={<Contact />} />
                      <Route path="/privacy" element={<Privacy />} />
                      <Route path="/terms" element={<Terms />} />
                      <Route path="/safety-guidelines" element={<SafetyGuidelines />} />
                      <Route path="/stores/:storeId" element={<StoreInfo />} />
                      <Route
                        path="/stores/liquor/:storeId"
                        element={<LiquorStoreInfo />}
                      />
                      <Route
                        path="/stores/liquor/:storeId/:type/:subType"
                        element={<LiquorStoreProducts />}
                      />
                      <Route path="/account/orders" element={<ProtectedRoute roles={['customer']}> <Orders /> </ProtectedRoute>} />
                      <Route path="/account/referrals" element={<ProtectedRoute roles={['customer']}> <Referrals /> </ProtectedRoute>} />
                      <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
                      <Route path="/checkout" element={<ProtectedRoute roles={['customer']}> <Checkout /> </ProtectedRoute>} />
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/auth/verify" element={<VerificationPage />} />
                      <Route path="/auth/reset" element={<ProtectedRoute><ResetPassword /></ProtectedRoute>} />
                      {/* <Route path="/ops/login" element={<OperationsLogin />} /> */}
                      <Route
                        path="/ops/register"
                        element={<OperationsRegister />}
                      />
                      <Route path="/ops/deliveries" element={<ProtectedRoute roles={['operations']}> <Deliveries /> </ProtectedRoute>} />
                      <Route path="/ops/" element={<ProtectedRoute roles={['operations']}> <OpsHome /> </ProtectedRoute>} />
                      <Route
                        path="/ops/transaction-progress"
                        element={<ProtectedRoute roles={['operations']}> <TransactionProgress /> </ProtectedRoute>}
                      />
                      {/* <Route path="/sup/login" element={<SupLogin />} /> */}
                      {/* <Route path="/sup/register" element={<SupRegister />} /> */}
                      <Route path="/sup/" element={<ProtectedRoute roles={['elite']}> <SupMain /> </ProtectedRoute>} />
                      <Route path="/sup/orders" element={<ProtectedRoute roles={['elite']}> <OrdersSummary /> </ProtectedRoute>} />
                      <Route path="/sup/stores/" element={<ProtectedRoute roles={['elite']}> <StoresSummary /> </ProtectedRoute>} />
                      <Route path="/sup/stores/add" element={<ProtectedRoute roles={['elite']}> <AddStoreForm /> </ProtectedRoute>} />
                      <Route
                        path="/sup/stores/:storeId"
                        element={<ProtectedRoute roles={['elite']}> <Products /> </ProtectedRoute>}
                      />
                      <Route
                        path="/sup/stores/:storeId/products/add"
                        element={<ProtectedRoute roles={['elite']}> <AddProductForm /> </ProtectedRoute>}
                      />
                      <Route
                        path="/sup/stores/:storeId/:productId"
                        element={<ProtectedRoute roles={['elite']}> <Product /> </ProtectedRoute>}
                      />
                      <Route
                        path="/sup/stores/:storeId/:productId/options/:optionId/edit"
                        element={<ProtectedRoute roles={['elite']}> <EditOption /> </ProtectedRoute>}
                      />
                      {/* <Route
                        path="/sup/stores/:storeId"
                        element={<ProtectedRoute roles={['elite']}> <StoresSummary /> </ProtectedRoute>}
                      /> */}
                      <Route
                        path="/checkout/progress"
                        element={<ProtectedRoute roles={['customer']}><TransactionInProgress /></ProtectedRoute>}
                      />
                      <Route
                        path="/settings/security"
                        element={<SecuritySettings />}
                      />
                      {/* <Route path="/store/login" element={<StoreLogin />} /> */}
                      <Route path="/store/register" element={<StoreRegister />} />
                      <Route path="/store/dashboard" element={<ProtectedRoute roles={['merchant']}> <StoreDashboard /> </ProtectedRoute>} />
                      <Route path="/store/orders" element={<ProtectedRoute roles={['merchant']}> <StoreOrders /> </ProtectedRoute>} />
                      <Route path="/store/catalog/:storeId" element={<ProtectedRoute roles={['merchant']}> <Catalog /> </ProtectedRoute>} />
                      <Route
                          path="/store/catalog/:storeId/:productId"
                          element={<ProtectedRoute roles={['merchant']}> <Product /> </ProtectedRoute>}
                      />
                      <Route
                          path="/store/catalog/:storeId/:productId/edit"
                          element={<ProtectedRoute roles={['merchant']}> <EditProduct /> </ProtectedRoute>}
                      />
                      <Route
                          path="/store/catalog/:storeId/add"
                          element={<ProtectedRoute roles={['merchant']}> <AddProductForm /> </ProtectedRoute>}
                      />
                      <Route
                          path="/store/catalog/:storeId/:productId/options/add"
                          element={<ProtectedRoute roles={['merchant']}> <AddOption /> </ProtectedRoute>}
                      />
                      <Route
                          path="/store/catalog/:storeId/:productId/options/:optionId/edit"
                          element={<ProtectedRoute roles={['merchant']}> <EditOption /> </ProtectedRoute>}
                      />
                      <Route path="/capacitor-test" element={<CapacitorTest />} />
                    </Routes>
                  </Container>
                </TabContextProvider>
              </ShoppingCartProvider>
              </ServiceAvailabilityProvider>
            </div>
            <Footer />
            {ReactQueryDevtools && (
              <Suspense fallback={null}>
                <ReactQueryDevtools initialIsOpen={false} />
              </Suspense>
            )}
          </MobileWrapper>
        </ThemeProvider>
      </QueryClientProvider>
    </UserContextProvider>
  );
};

const ProtectedRoute = ({ children, roles }) => {
  const { isAuthenticated, isLoading } = useUserContext();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      saveReturnPath(location);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isAuthenticated]);

  if (isLoading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export default App;
